import React, { useEffect, useState, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Sparkles, Rocket, Cog, Zap, Calendar, ExternalLink, Loader2, ArrowRight, Search, Link2,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";

import useDocumentTitle from "../hooks/useDocumentTitle";
import usePageMeta from "../hooks/usePageMeta";
import M from "../lib/pageMetaConfig";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

// Customer-facing "What's New" — public, no auth. Curated subset of
// PRODUCT_WINS filtered through the backend's PUBLIC_WHATS_NEW_TAGS
// allowlist so internal A/B-winner promotions and admin-tooling launches
// stay private.
const TAG_META = {
  feature:     { color: "#1FDD83", icon: Sparkles, label: "FEATURE"     },
  integration: { color: "#22D3EE", icon: Cog,      label: "INTEGRATION" },
  developer:   { color: "#22D3EE", icon: Rocket,   label: "DEVELOPER"   },
  billing:     { color: "#FB923C", icon: Zap,      label: "BILLING"     },
  ux:          { color: "#A78BFA", icon: Sparkles, label: "POLISH"      },
};

function tagInfo(tag) {
  return TAG_META[tag] || { color: "#9CAEC9", icon: Sparkles, label: (tag || "UPDATE").toUpperCase() };
}

// Iter269.20b — Group entries by relative date bucket so the timeline
// becomes scannable for visitors evaluating release cadence.
function groupByDate(items) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const weekAgo = new Date(today); weekAgo.setDate(today.getDate() - 7);
  const monthAgo = new Date(today); monthAgo.setMonth(today.getMonth() - 1);

  const buckets = new Map();
  for (const item of items) {
    const d = new Date(item.date + "T12:00:00Z");
    let bucket;
    if (d >= today) bucket = "Today";
    else if (d >= weekAgo) bucket = "This week";
    else if (d >= monthAgo) bucket = "This month";
    else {
      bucket = d.toLocaleString("en-US", { month: "long", year: "numeric" });
    }
    if (!buckets.has(bucket)) buckets.set(bucket, []);
    buckets.get(bucket).push(item);
  }
  return Array.from(buckets.entries());
}

export default function WhatsNew() {
  usePageMeta(M.whatsNew);
  const [items, setItems]     = useState(null);
  const [error, setError]     = useState("");
  const [filter, setFilter]   = useState("all");
  const [search, setSearch]   = useState("");
  const location              = useLocation();

  useEffect(() => {
    fetch(`${API}/api/whats-new?limit=40`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((d) => setItems(d.items || []))
      .catch(() => setError("Could not load updates. Please try refreshing."));
  }, []);

  // Iter269.20b — Scroll to the anchored entry once items are loaded
  // (deep-links like /whats-new#iter269-stripe-live-billing).
  useEffect(() => {
    if (!items || !location.hash) return;
    const id = location.hash.slice(1);
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("whatsnew-anchor-highlight");
      setTimeout(() => el.classList.remove("whatsnew-anchor-highlight"), 2400);
    }
  }, [items, location.hash]);

  const filterOptions = [
    { key: "all",         label: "All updates" },
    { key: "feature",     label: "Features"    },
    { key: "developer",   label: "Developer"   },
    { key: "billing",     label: "Pricing"     },
  ];

  // Iter269.20b — Apply both tag filter + free-text search.
  const visible = useMemo(() => {
    if (!items) return null;
    let out = filter === "all" ? items : items.filter((i) => i.tag === filter);
    const q = search.trim().toLowerCase();
    if (q) {
      out = out.filter((i) =>
        (i.title    || "").toLowerCase().includes(q) ||
        (i.summary  || "").toLowerCase().includes(q) ||
        (i.tag      || "").toLowerCase().includes(q) ||
        (i.id       || "").toLowerCase().includes(q),
      );
    }
    return out;
  }, [items, filter, search]);

  const grouped = useMemo(
    () => (visible && visible.length > 0 ? groupByDate(visible) : []),
    [visible],
  );

  return (
    <div className="min-h-screen qt-radial-bg">
      <Header />
      <main className="max-w-[1100px] mx-auto px-6 sm:px-8 py-10" data-testid="whatsnew-page">
        <section className="mb-10">
          <div className="qt-label" style={{ color: "var(--qt-cyan)" }}>
            WHAT'S NEW · QORTRACE
          </div>
          <h1 className="qt-h2 text-4xl sm:text-5xl mt-2" data-testid="whatsnew-title">
            Recent platform updates
          </h1>
          <p
            className="mt-3 max-w-2xl text-base"
            style={{ color: "var(--qt-text-2)", lineHeight: 1.7 }}
          >
            New features, integrations, and pricing changes — the customer-meaningful changes
            we release. Subscribe to platform updates from your{" "}
            <Link to="/account" className="underline" style={{ color: "var(--qt-cyan)" }}>
              account preferences
            </Link>{" "}
            once you're signed in.
          </p>
        </section>

        <div className="flex flex-col sm:flex-row gap-3 sm:items-center mb-6" data-testid="whatsnew-controls">
          {/* Iter269.20b — Free-text search across title/summary/tag */}
          <div
            className="flex items-center gap-2 px-3 py-2 rounded-full flex-1 max-w-md"
            style={{
              background:  "rgba(255,255,255,0.04)",
              border:      "1px solid rgba(255,255,255,0.10)",
            }}
          >
            <Search size={14} style={{ color: "var(--qt-text-3)" }} aria-hidden />
            <input
              type="text"
              placeholder="Search releases..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="whatsnew-search-input"
              className="bg-transparent border-none outline-none text-sm flex-1"
              style={{ color: "#FFFFFF", fontFamily: "monospace" }}
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                data-testid="whatsnew-search-clear"
                className="text-xs px-2 py-0.5 rounded"
                style={{ color: "var(--qt-text-3)" }}
                title="Clear search"
              >
                ✕
              </button>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-8" data-testid="whatsnew-filter-bar">
          {filterOptions.map((opt) => (
            <button
              key={opt.key}
              onClick={() => setFilter(opt.key)}
              data-testid={`whatsnew-filter-${opt.key}`}
              className="rounded-full px-4 py-1.5 text-xs uppercase tracking-wider transition"
              style={{
                fontFamily: "monospace",
                letterSpacing: "0.12em",
                border: filter === opt.key
                  ? "1px solid var(--qt-cyan)"
                  : "1px solid rgba(255,255,255,0.14)",
                background: filter === opt.key
                  ? "rgba(0,229,255,0.12)"
                  : "transparent",
                color: filter === opt.key ? "var(--qt-cyan)" : "var(--qt-text-2)",
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {error && (
          <div
            className="rounded-lg border p-4 text-sm"
            style={{
              borderColor: "rgba(255,90,110,0.35)",
              background:  "rgba(255,90,110,0.08)",
              color:       "#FF5A6E",
            }}
            data-testid="whatsnew-error"
          >
            {error}
          </div>
        )}

        {!visible && !error && (
          <div className="flex items-center gap-3 opacity-70" data-testid="whatsnew-loading">
            <Loader2 size={16} className="animate-spin" /> Loading updates…
          </div>
        )}

        {visible && visible.length === 0 && (
          <div
            className="rounded-lg border p-6 text-sm opacity-80"
            style={{
              borderColor: "rgba(255,255,255,0.10)",
              background:  "rgba(255,255,255,0.02)",
            }}
            data-testid="whatsnew-empty"
          >
            No entries match this filter yet. Check back soon — we release updates every week.
          </div>
        )}

        {visible && visible.length > 0 && (
          <div data-testid="whatsnew-feed">
            {grouped.map(([bucketLabel, entries], gIdx) => (
              <section key={bucketLabel} className="mb-10" data-testid={`whatsnew-bucket-${bucketLabel.toLowerCase().replace(/\s+/g, '-')}`}>
                <div
                  className="qt-label mb-4 flex items-center gap-3"
                  style={{ color: "var(--qt-text-3)", letterSpacing: "0.2em" }}
                >
                  <span>{bucketLabel.toUpperCase()}</span>
                  <span
                    className="inline-flex items-center justify-center rounded-full text-[10px] font-mono"
                    style={{
                      minWidth: 22,
                      height: 18,
                      padding: "0 6px",
                      background: "rgba(0,229,255,0.08)",
                      border: "1px solid rgba(0,229,255,0.2)",
                      color: "var(--qt-cyan)",
                    }}
                  >
                    {entries.length}
                  </span>
                  <span
                    className="flex-1 h-px"
                    style={{ background: "rgba(255,255,255,0.06)" }}
                    aria-hidden
                  />
                </div>
                <ol className="relative">
                  <div
                    className="absolute left-[7px] top-0 bottom-0"
                    style={{ width: 2, background: "rgba(0,229,255,0.16)" }}
                    aria-hidden
                  />
                  {entries.map((it, idx) => (
                    <Entry key={it.id} entry={it} first={gIdx === 0 && idx === 0} />
                  ))}
                </ol>
              </section>
            ))}
          </div>
        )}

        {/* Bottom upgrade nudge — soft "follow the platform" CTA. */}
        <div
          className="mt-12 qt-card text-center"
          style={{
            background: "linear-gradient(135deg, rgba(0,229,255,0.06), rgba(0,229,255,0.02))",
            borderColor: "rgba(0,229,255,0.3)",
          }}
        >
          <div className="qt-label" style={{ color: "var(--qt-cyan)" }}>STAY AHEAD OF THE QUANTUM CURVE</div>
          <h3 className="text-2xl font-semibold mt-2" style={{ color: "#fff" }}>
            Run a free wallet scan in 90 seconds
          </h3>
          <p className="mt-2 text-sm max-w-xl mx-auto" style={{ color: "var(--qt-text-2)" }}>
            See the platform live — drop in a wallet address and get an instant post-quantum
            exposure score across 18 chains. No signup required.
          </p>
          <div className="mt-5 flex flex-wrap gap-3 justify-center">
            <Link to="/scan" className="qt-btn qt-btn-primary" data-testid="whatsnew-cta-scan">
              Run a free scan <ArrowRight size={14} />
            </Link>
            <Link to="/pricing" className="qt-btn qt-btn-secondary" data-testid="whatsnew-cta-pricing">
              See pricing
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function Entry({ entry, first }) {
  const meta = tagInfo(entry.tag);
  const Icon = meta.icon;
  const [copied, setCopied] = React.useState(false);

  // Iter269.20b — Per-entry deep-link. Falls back gracefully if the
  // browser denies clipboard access.
  const copyAnchorLink = (e) => {
    e.stopPropagation();
    const url = `${window.location.origin}/whats-new#${entry.id}`;
    try {
      navigator.clipboard.writeText(url).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      });
    } catch {
      // No-op — old browsers without clipboard API still see the anchor in URL.
    }
    // Update URL hash for in-page reference even if clipboard fails
    if (window.history && window.history.replaceState) {
      window.history.replaceState(null, "", `#${entry.id}`);
    }
  };

  return (
    <li
      id={entry.id}
      className="relative pl-10 pb-8 scroll-mt-24"
      data-testid={`whatsnew-entry-${entry.id}`}
    >
      <div
        className="absolute left-0 top-1.5 flex h-4 w-4 items-center justify-center rounded-full"
        style={{
          background: first ? meta.color : "#0B1324",
          border: `2px solid ${meta.color}`,
          boxShadow: first ? `0 0 12px ${meta.color}66` : "none",
        }}
        aria-hidden
      />
      <div
        className="rounded-xl border p-5 transition hover:translate-x-[2px] relative group"
        style={{
          borderColor: "rgba(255,255,255,0.08)",
          background:  "rgba(11,19,36,0.8)",
        }}
      >
        {/* Anchor copy button — appears on hover */}
        <button
          onClick={copyAnchorLink}
          data-testid={`whatsnew-entry-anchor-${entry.id}`}
          className="absolute top-3 right-3 inline-flex items-center gap-1 text-[10px] qt-mono rounded px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity"
          style={{
            background: copied ? "rgba(31,221,131,0.15)" : "rgba(255,255,255,0.04)",
            border:     copied ? "1px solid rgba(31,221,131,0.5)" : "1px solid rgba(255,255,255,0.10)",
            color:      copied ? "#1FDD83" : "var(--qt-text-3)",
            letterSpacing: "0.08em",
            textTransform: "uppercase",
          }}
          title={copied ? "Link copied!" : "Copy direct link"}
        >
          <Link2 size={10} />
          {copied ? "Copied" : "Link"}
        </button>

        <div className="flex flex-wrap items-center gap-3 text-[11px] qt-mono pr-16" style={{ letterSpacing: "0.12em" }}>
          <span
            className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1"
            style={{
              border: `1px solid ${meta.color}`,
              background: `${meta.color}1F`,
              color: meta.color,
              textTransform: "uppercase",
            }}
            data-testid="whatsnew-entry-tag"
          >
            <Icon size={11} /> {meta.label}
          </span>
          <span
            className="inline-flex items-center gap-1.5"
            style={{ color: "var(--qt-text-3)", textTransform: "uppercase" }}
          >
            <Calendar size={11} /> {entry.date || "—"}
          </span>
        </div>

        <h2
          className="mt-3 text-lg font-semibold leading-tight"
          style={{ color: "#FFFFFF" }}
          data-testid="whatsnew-entry-title"
        >
          {entry.title}
        </h2>

        <p
          className="mt-2 text-sm"
          style={{ color: "var(--qt-text-2)", lineHeight: 1.7 }}
          data-testid="whatsnew-entry-summary"
        >
          {entry.summary}
        </p>

        {entry.url && (
          <a
            href={entry.url}
            className="mt-3 inline-flex items-center gap-1.5 text-xs qt-mono hover:opacity-80"
            style={{
              color: meta.color,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
            }}
            data-testid="whatsnew-entry-link"
          >
            Try it <ExternalLink size={11} />
          </a>
        )}
      </div>
    </li>
  );
}
